/**
 * Query-targeted landing content for service and solution detail pages: quick answer, example
 * price/timeline bands (read from projectCostBands.shared.js, never retyped), case studies and
 * extra sections. Pages without `landing` keep their original layout.
 */
import {
  PROJECT_COST_BANDS,
  PROJECT_COST_PROFILE_LABEL,
  PROJECT_COST_SCOPE_LABELS,
  formatProjectCostDuration,
  formatProjectCostRange,
} from '@/lib/projectCostBands.shared.js';
import type { ProjectCostProfile } from '@/lib/projectCostEstimate';
import type { PortfolioProjectKey } from '@/lib/portfolioProjects';

type L = { tr: string; en: string };

export type LandingLang = 'tr' | 'en';

export type LandingBandScope = 'mvp' | 'platform';

export type LandingCostBand = {
  profile: ProjectCostProfile;
  scope: LandingBandScope;
  /** Approved timeline copy that replaces the band's computed duration (mobile MVP). */
  duration?: L;
};

export type LandingSection = {
  title: L;
  items: L[];
};

export type LandingContent = {
  /** 40–60 words once tokens are resolved; `{price:profile.scope}` / `{duration:profile.scope}` read the cost bands. */
  quickAnswer: L;
  costBands: LandingCostBand[];
  /** Shown under the bands when no estimator profile matches the page exactly. */
  costBandNote?: L;
  caseStudies: PortfolioProjectKey[];
  /** Replaces the default case-study note when the projects are adjacent rather than the same service type. */
  caseStudyNote?: L;
  sections?: LandingSection[];
  guideSlugs?: string[];
};

export const MOBILE_MVP_DURATION: L = {
  tr: 'projeye göre 1–2 haftadan 6–8 haftaya',
  en: '1–2 weeks to 6–8 weeks, depending on the project',
};

export const LANDING_COPY = {
  quickAnswerLabel: { tr: 'Hızlı cevap', en: 'Quick answer' },
  bandsTitle: { tr: 'Örnek fiyat ve süre bandı', en: 'Example price and timeline band' },
  bandsLabel: {
    tr: 'Örnek bant · KDV hariç · Kesin fiyat proje analizinden sonra belirlenir',
    en: 'Example band · excl. VAT · Final price after project analysis',
  },
  bandsIntro: {
    tr: 'Rakamlar ücretsiz proje maliyet tahmincimizdeki bantlardan gelir (TRY). Sabit fiyat listesi değildir: fiyat, proje analizinden sonra iş listesi, efor ve kurulan outsource ekibin maliyetiyle yazılı teklifte hesaplanır.',
    en: 'Figures come from the bands in our free project cost estimator (TRY). This is not a fixed price list: the price is calculated in a written proposal after the project analysis, from the task list, the effort and the cost of the outsourced team assembled for it.',
  },
  profileColumn: { tr: 'Proje profili', en: 'Project profile' },
  scopeColumn: { tr: 'Kapsam', en: 'Scope' },
  priceColumn: { tr: 'Örnek bant', en: 'Example band' },
  durationColumn: { tr: 'Süre', en: 'Timeline' },
  estimatorLink: { tr: 'Kendi projeniz için tahmin alın: proje maliyet tahmincisi', en: 'Get an estimate for your project: project cost estimator' },
  caseStudiesTitle: { tr: 'İlgili case study’ler', en: 'Related case studies' },
  caseStudiesNote: {
    tr: 'Yalnızca yayında olan projelerimiz; her sayfada Figtures’ın rolü ve müşteriye ait bilgiler ayrı gösterilir.',
    en: 'Published projects only; each page separates Figtures’ role from client-owned information.',
  },
  toolsTitle: { tr: 'Ücretsiz araçlar', en: 'Free tools' },
  guidesTitle: { tr: 'Derinlemesine rehberler', en: 'In-depth guides' },
} satisfies Record<string, L>;

export type LandingBandRow = {
  profileLabel: string;
  scopeLabel: string;
  range: string;
  duration: string;
};

const SCOPE_INDEX: Record<LandingBandScope, number> = { mvp: 0, platform: 1 };

function bandFor(profile: ProjectCostProfile, scope: LandingBandScope) {
  return PROJECT_COST_BANDS[profile][scope];
}

export function getLandingBandRows(bands: LandingCostBand[], lang: LandingLang): LandingBandRow[] {
  return bands.map(({ profile, scope, duration }) => {
    const band = bandFor(profile, scope);
    return {
      profileLabel: PROJECT_COST_PROFILE_LABEL[profile][lang],
      scopeLabel: PROJECT_COST_SCOPE_LABELS[lang][SCOPE_INDEX[scope]],
      range: formatProjectCostRange(band.min, band.max, lang),
      duration: duration ? duration[lang] : formatProjectCostDuration(band.weeksMin, band.weeksMax, lang),
    };
  });
}

const BAND_TOKEN = /\{(price|duration):([a-z_]+)\.(mvp|platform)\}/g;

/** Replaces `{price:saas_mvp.mvp}` / `{duration:saas_mvp.mvp}` with the current band values. */
export function resolveLandingText(text: string, lang: LandingLang): string {
  return text.replace(BAND_TOKEN, (token, kind: string, profile: string, scope: LandingBandScope) => {
    if (!(profile in PROJECT_COST_BANDS)) throw new Error(`Unknown cost profile in ${token}`);
    const band = bandFor(profile as ProjectCostProfile, scope);
    return kind === 'price'
      ? formatProjectCostRange(band.min, band.max, lang)
      : formatProjectCostDuration(band.weeksMin, band.weeksMax, lang);
  });
}

export function resolveLandingFaqs<T extends { question: L; answer: L }>(faqs: T[]): T[] {
  return faqs.map((faq) => ({
    ...faq,
    answer: { tr: resolveLandingText(faq.answer.tr, 'tr'), en: resolveLandingText(faq.answer.en, 'en') },
  }));
}

export function countLandingWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}
